/**
 * Shared WebSocket service for real-time messaging and notifications
 * Connects to the NestJS MessagesGateway via Socket.io
 */

import { io, Socket } from 'socket.io-client';
import { WS_CONFIG } from '@/config/api.config';
import { auth } from '@/lib/firebase';

type EventHandler = (data: any) => void;

class WebSocketService {
  private socket: Socket | null = null;
  private listeners = new Map<string, Set<EventHandler>>();
  private connecting = false;
  private connected = false;

  async connect(): Promise<void> {
    if (this.connected || this.connecting) return;
    this.connecting = true;

    try {
      const fbUser = auth.currentUser;
      if (!fbUser) {
        this.connecting = false;
        return;
      }

      const token = await fbUser.getIdToken();

      this.socket = io(`${WS_CONFIG.url}/ws/messages`, {
        auth: { token },
        reconnection: WS_CONFIG.reconnection,
        reconnectionDelay: WS_CONFIG.reconnectionDelay,
        reconnectionAttempts: WS_CONFIG.reconnectionAttempts,
        transports: ['websocket'],
      });

      this.socket.on('connect', () => {
        this.connected = true;
        this.connecting = false;
        console.log('🔌 WebSocket connected');
      });

      this.socket.on('disconnect', () => {
        this.connected = false;
        console.log('🔌 WebSocket disconnected');
      });

      // Forward all known events to registered listeners
      const events = [
        'new_message',
        'user_typing',
        'new_contact_request',
        'contact_request_updated',
        'notification',
      ];

      events.forEach((event) => {
        this.socket?.on(event, (data: any) => {
          const handlers = this.listeners.get(event);
          if (handlers) {
            handlers.forEach((fn) => fn(data));
          }
        });
      });
    } catch (error) {
      console.error('WebSocket connection error:', error);
      this.connecting = false;
    }
  }

  disconnect(): void {
    this.socket?.disconnect();
    this.socket = null;
    this.connected = false;
    this.connecting = false;
  }

  on(event: string, handler: EventHandler): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(handler);

    // Also register on the socket directly for events added after connect
    if (this.socket) {
      this.socket.on(event, handler);
    }

    // Return unsubscribe function
    return () => {
      this.listeners.get(event)?.delete(handler);
      this.socket?.off(event, handler);
    };
  }

  emit(event: string, data?: any): void {
    this.socket?.emit(event, data);
  }

  get isConnected(): boolean {
    return this.connected;
  }
}

// Singleton instance
export const wsService = new WebSocketService();
